import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Send, Paperclip, X, MessageCircle, ChevronLeft, Check, CheckCheck } from "lucide-react";
import { API, WS_BASE, BACKEND_URL, errText, fmtTime, fmtDate, playPing } from "../lib/api";
import { ImageLightbox, VideoThumb } from "../components/ImageLightbox";

const fileUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);
const LS_KEY = "sd_visitor";
const MAX_UPLOAD = 25 * 1024 * 1024;
const BASE_TITLE = document.title || "StarAndDaisy Support";

const Bubble = ({ m }) => {
  const [lightbox, setLightbox] = useState(false);
  const mine = m.sender === "customer";
  const cls = m.sender === "system" ? "cx-msg-sys" : mine ? "cx-msg-out" : "cx-msg-in";
  return (
    <div className={`flex ${m.sender === "system" ? "justify-center" : mine ? "justify-end" : "justify-start"} fade-up`}>
      <div className={`${cls} px-3.5 py-2 max-w-[80%] text-sm`}>
        {m.attachment && (
          <div className="mb-1">
            {m.type === "image" ? <>
                <img src={fileUrl(m.attachment.url)} alt={m.attachment.name} data-testid="cx-image-attachment" onClick={() => setLightbox(true)} className="max-w-[220px] max-h-[180px] rounded-xl object-cover cursor-zoom-in" />
                {lightbox && <ImageLightbox src={fileUrl(m.attachment.url)} alt={m.attachment.name} onClose={() => setLightbox(false)} />}
              </>
              : m.type === "video" ? <>
                <VideoThumb src={fileUrl(m.attachment.url)} testId="cx-video-attachment" className="w-[220px] h-[150px]" onOpen={() => setLightbox(true)} />
                {lightbox && <ImageLightbox src={fileUrl(m.attachment.url)} alt={m.attachment.name} type="video" onClose={() => setLightbox(false)} />}
              </>
              : <a href={fileUrl(m.attachment.url)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 underline text-sm"><Paperclip size={13} />{m.attachment.name}</a>}
          </div>
        )}
        {m.order_card && (
          <div data-testid="cx-order-card" className="mt-1 mb-1 rounded-xl border border-slate-200 bg-white text-slate-800 p-2.5 text-xs min-w-[200px] shadow-sm">
            <div className="flex justify-between font-bold gap-3"><span>Order #{m.order_card.number}</span><span className="uppercase text-[10px] text-blue-600">{m.order_card.status}</span></div>
            <div className="text-slate-500">{fmtDate(m.order_card.date)} · ₹{m.order_card.total}{m.order_card.payment_method ? ` · ${m.order_card.payment_method}` : ""}</div>
            {m.order_card.tracking && <div className="font-bold text-blue-700">🚚 {m.order_card.tracking}</div>}
            {(m.order_card.items || []).slice(0, 3).map((i, idx) => <div key={idx} className="truncate">• {i.name} ×{i.qty}</div>)}
          </div>
        )}
        {m.text && <span className="whitespace-pre-wrap break-words">{m.text}</span>}
        <div className={`flex items-center gap-1 justify-end mt-0.5 text-[10px] ${mine ? "text-blue-100" : "text-slate-400"}`}>
          {fmtTime(m.created_at)}
          {mine && (m.status === "seen" ? <CheckCheck size={12} /> : m.status === "delivered" ? <CheckCheck size={12} className="opacity-60" /> : <Check size={12} className="opacity-60" />)}
        </div>
      </div>
    </div>
  );
};

export default function CustomerChat() {
  const embed = new URLSearchParams(window.location.search).get("embed") === "1";
  const [cfg, setCfg] = useState({ color: "#2563EB", title: "StarAndDaisy Support", greeting: "Tell us who you are so we can help you faster and keep your chat history." });
  const [session, setSession] = useState(() => { try { return JSON.parse(localStorage.getItem(LS_KEY)); } catch (e) { return null; } });
  const [form, setForm] = useState({ name: "", contact: "" });
  const [formErr, setFormErr] = useState("");
  const [convs, setConvs] = useState([]);
  const [view, setView] = useState("list"); // list | chat
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [typing, setTyping] = useState(false);
  const [online, setOnline] = useState(false);
  const [csatSel, setCsatSel] = useState(0);
  const [uploading, setUploading] = useState(0);
  const [uploadErr, setUploadErr] = useState("");
  const wsRef = useRef(null);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const typingTimer = useRef(null);
  const unreadRef = useRef(0);
  const convIdRef = useRef(null);
  const reconnectTimer = useRef(null);
  const flashTimer = useRef(null);
  const cfgRef = useRef(cfg);
  cfgRef.current = cfg;
  const vtRef = useRef(null);
  const pendingStartRef = useRef(null);

  const vt = session?.token;
  vtRef.current = vt;

  useEffect(() => {
    axios.get(`${API}/widget/config`)
      .then((r) => setCfg(r.data))
      .catch(() => {})
      .finally(() => { if (embed) window.parent.postMessage({ type: "snd-ready" }, "*"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanStart = (d) => {
    const name = String(d.name || "").trim().slice(0, 80);
    let digits = String(d.phone || "").replace(/\D/g, "");
    if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
    const phone = /^[6-9]\d{9}$/.test(digits) ? `+91${digits}` : "";
    const order = String(d.order || "").replace(/\D/g, "").slice(0, 12);
    const message = String(d.message || "").trim().slice(0, 500);
    return { name, phone, order, message };
  };

  const isDuplicateStart = (sig) => {
    try {
      const rec = JSON.parse(sessionStorage.getItem("sd_start_dedup") || "null");
      return !!rec && rec.sig === sig && Date.now() - rec.ts < 10 * 60 * 1000;
    } catch (e) { return false; }
  };

  const autoSend = async (token, d) => {
    if (!d.message) return;
    const sig = `${d.phone}|${d.order}|${d.message}`;
    if (isDuplicateStart(sig)) return;
    try {
      const { data: list } = await axios.get(`${API}/widget/conversations`, { params: { vt: token } });
      let target = (list || []).find((c) => c.status === "open");
      if (!target) {
        const r = await axios.post(`${API}/widget/conversations`, { vt: token, source: "snd.in link", order_number: d.order || null });
        target = r.data;
      }
      const text = d.order ? `Order #${d.order}: ${d.message}` : d.message;
      await axios.post(`${API}/widget/messages`, { vt: token, conversation_id: target.id, type: "text", text, order_number: d.order || null });
      try { sessionStorage.setItem("sd_start_dedup", JSON.stringify({ sig, ts: Date.now() })); } catch (e) { /* ignore */ }
      const { data: md } = await axios.get(`${API}/widget/messages/${target.id}`, { params: { vt: token } });
      convIdRef.current = target.id;
      setConv(md.conversation); setMessages(md.messages); setView("chat");
      connectWs(target.id);
    } catch (e) { /* no content logging */ }
  };

  const handleStart = async (raw) => {
    pendingStartRef.current = null;
    const d = cleanStart(raw);
    const token = vtRef.current;
    if (token) { await autoSend(token, d); return; }
    if (d.name && d.phone) {
      try {
        const { data } = await axios.post(`${API}/widget/session`, { name: d.name, phone: d.phone, source: "link" });
        const sess = { token: data.visitor_token, name: data.customer.name };
        localStorage.setItem(LS_KEY, JSON.stringify(sess));
        vtRef.current = data.visitor_token;
        setSession(sess);
        await autoSend(data.visitor_token, d);
        return;
      } catch (e) { /* fall through to prefilled form */ }
    }
    setForm((f) => ({ name: d.name || f.name, contact: d.phone ? d.phone.replace("+91", "") : f.contact }));
    if (d.message || d.order) pendingStartRef.current = d;
  };

  const askNotifPermission = () => {
    try {
      if ("Notification" in window && Notification.permission === "default") Notification.requestPermission().catch(() => {});
    } catch (e) { /* ignore */ }
  };

  const startTitleFlash = () => {
    if (embed || flashTimer.current) return;
    let on = false;
    flashTimer.current = setInterval(() => { document.title = on ? BASE_TITLE : "💬 New reply!"; on = !on; }, 1200);
  };
  const stopTitleFlash = useCallback(() => {
    if (flashTimer.current) { clearInterval(flashTimer.current); flashTimer.current = null; document.title = BASE_TITLE; }
  }, []);

  const notifyIncoming = (msg) => {
    try { playPing(); } catch (e) { /* ignore */ }
    if (document.hidden) {
      startTitleFlash();
      try {
        if ("Notification" in window && Notification.permission === "granted") {
          const n = new Notification(cfgRef.current.title || "Support", { body: msg.text || `New ${msg.type || "message"} from support`, tag: "snd-chat" });
          n.onclick = () => { window.focus(); n.close(); };
        }
      } catch (e) { /* ignore */ }
    }
  };

  const loadConvs = useCallback(async () => {
    if (!vt) return;
    try {
      const { data } = await axios.get(`${API}/widget/conversations`, { params: { vt } });
      setConvs(data);
      return data;
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem(LS_KEY); setSession(null); }
      return [];
    }
  }, [vt]);

  useEffect(() => {
    if (!vt) return;
    axios.get(`${API}/widget/status`).then((r) => setOnline(r.data.agents_online));
    loadConvs().then((data) => {
      const open = (data || []).find((c) => c.status === "open");
      if (open) openConv(open);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vt]);

  const syncMessages = useCallback(async (convId) => {
    try {
      const token = vtRef.current;
      const { data } = await axios.get(`${API}/widget/messages/${convId}`, { params: { vt: token } });
      if (convIdRef.current !== convId) return;
      setConv(data.conversation);
      setMessages((prev) => {
        const pl = prev[prev.length - 1];
        const nl = data.messages[data.messages.length - 1];
        if (prev.length === data.messages.length && pl?.id === nl?.id && pl?.status === nl?.status) return prev;
        return data.messages;
      });
      axios.post(`${API}/widget/seen/${convId}`, { vt: token }).catch(() => {});
    } catch (e) { /* offline — poll will retry */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openConv = async (c) => {
    const { data } = await axios.get(`${API}/widget/messages/${c.id}`, { params: { vt } });
    convIdRef.current = c.id;
    setConv(data.conversation); setMessages(data.messages); setView("chat");
    axios.post(`${API}/widget/seen/${c.id}`, { vt }).catch(() => {});
    askNotifPermission();
    connectWs(c.id);
  };

  const closeWs = () => {
    if (wsRef.current) { wsRef.current.__manual = true; try { wsRef.current.close(); } catch (e) { /* ignore */ } }
    clearTimeout(reconnectTimer.current);
  };

  const connectWs = (convId) => {
    closeWs();
    let ws;
    try { ws = new WebSocket(`${WS_BASE}/ws/visitor/${convId}?vt=${vtRef.current}`); } catch (e) { scheduleReconnect(convId); return; }
    wsRef.current = ws;
    ws.onopen = () => { if (convIdRef.current === convId) syncMessages(convId); };
    ws.onclose = () => { if (!ws.__manual && convIdRef.current === convId) scheduleReconnect(convId); };
    ws.onmessage = (ev) => {
      const d = JSON.parse(ev.data);
      if (d.event === "message") {
        setMessages((prev) => (prev.some((m) => m.id === d.message.id) ? prev : [...prev, d.message]));
        setTyping(false);
        if (d.message.sender !== "customer") {
          axios.post(`${API}/widget/seen/${convId}`, { vt }).catch(() => {});
          if (d.message.sender !== "system" || true) notifyIncoming(d.message);
          if (embed) {
            unreadRef.current += 1;
            window.parent.postMessage({ type: "snd-unread", count: unreadRef.current }, "*");
          }
        }
      }
      if (d.event === "typing" && d.from === "agent") {
        setTyping(true);
        clearTimeout(typingTimer.current);
        typingTimer.current = setTimeout(() => setTyping(false), 2500);
      }
      if (d.event === "seen" && d.by === "agent") {
        setMessages((prev) => prev.map((m) => (m.sender === "customer" ? { ...m, status: "seen" } : m)));
      }
      if (d.event === "conversation" && d.conversation?.id === convId) setConv(d.conversation);
      if (d.event === "message_update") setMessages((prev) => prev.map((m) => (m.id === d.message.id ? d.message : m)));
      if (d.event === "presence") setOnline(!!d.agents_online);
    };
  };

  const scheduleReconnect = (convId) => {
    clearTimeout(reconnectTimer.current);
    reconnectTimer.current = setTimeout(() => {
      if (convIdRef.current === convId) connectWs(convId);
    }, 3000);
  };

  // Polling fallback: keep thread fresh even if WS silently dies
  useEffect(() => {
    const t = setInterval(() => {
      const id = convIdRef.current;
      if (!id || document.hidden) return;
      if (!wsRef.current || wsRef.current.readyState !== 1) connectWs(id);
      else syncMessages(id);
    }, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vt]);

  // On tab becoming visible / window focus: resync instantly + stop alerts
  useEffect(() => {
    const onWake = () => {
      stopTitleFlash();
      unreadRef.current = 0;
      const id = convIdRef.current;
      if (!id) return;
      if (!wsRef.current || wsRef.current.readyState !== 1) connectWs(id);
      else syncMessages(id);
    };
    const onVis = () => { if (!document.hidden) onWake(); };
    window.addEventListener("focus", onWake);
    document.addEventListener("visibilitychange", onVis);
    const onMsg = (e) => {
      if (e.data?.type === "snd-opened") { unreadRef.current = 0; const id = convIdRef.current; if (id) syncMessages(id); }
      if (e.data?.type === "snd-start" && embed) {
        const allowed = cfgRef.current.start_origins || [];
        const isLocalDev = /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(e.origin);
        if (!allowed.includes(e.origin) && !isLocalDev) return;
        handleStart(e.data);
      }
    };
    window.addEventListener("message", onMsg);
    return () => { window.removeEventListener("focus", onWake); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("message", onMsg); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vt]);

  useEffect(() => () => { closeWs(); stopTitleFlash(); }, [stopTitleFlash]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, typing]);

  const startSession = async (e) => {
    e.preventDefault();
    setFormErr("");
    askNotifPermission();
    const isEmail = form.contact.includes("@");
    try {
      const { data } = await axios.post(`${API}/widget/session`, {
        name: form.name, [isEmail ? "email" : "phone"]: form.contact,
        visitor_token: session?.token,
      });
      const sess = { token: data.visitor_token, name: data.customer.name };
      localStorage.setItem(LS_KEY, JSON.stringify(sess));
      vtRef.current = data.visitor_token;
      setSession(sess);
      if (pendingStartRef.current) {
        const pending = pendingStartRef.current;
        pendingStartRef.current = null;
        await autoSend(data.visitor_token, pending);
      }
    } catch (err) { setFormErr(errText(err)); }
  };

  const startNewConv = async () => {
    const { data } = await axios.post(`${API}/widget/conversations`, { vt });
    await loadConvs();
    openConv(data);
  };

  const send = async (payload) => {
    try {
      await axios.post(`${API}/widget/messages`, { vt, conversation_id: conv.id, ...payload });
    } catch (e) { setUploadErr("Message failed to send — please try again."); }
  };

  const pickOption = async (option) => {
    try {
      await axios.post(`${API}/widget/quick-option`, { vt, conversation_id: conv.id, option });
    } catch (e) { /* keep quiet */ }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    send({ type: "text", text: text.trim() });
    setText("");
  };

  const onType = (v) => {
    setText(v);
    if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify({ event: "typing" }));
  };

  const uploadFile = async (f) => {
    if (!f) return;
    setUploadErr("");
    if (f.size > MAX_UPLOAD) {
      setUploadErr("File is too large — max 25 MB. Please trim the video or send a shorter clip.");
      return;
    }
    const fd = new FormData(); fd.append("file", f);
    setUploading(1);
    try {
      const { data } = await axios.post(`${API}/upload`, fd, {
        onUploadProgress: (ev) => setUploading(Math.min(99, Math.max(1, Math.round((ev.loaded / (ev.total || f.size)) * 100)))),
      });
      await send({ type: data.kind, text: "", attachment: { url: data.url, name: data.name, content_type: data.content_type, size: data.size } });
    } catch (e) {
      setUploadErr(e.response?.status === 413 ? "File is too large — max 25 MB." : (errText(e) || "Upload failed — check your connection and try again."));
    }
    setUploading(0);
  };

  const Header = ({ back }) => (
    <div className="flex items-center gap-3 px-4 py-3 text-white shrink-0" style={{ background: cfg.color }}>
      {back && <button data-testid="cx-back-button" onClick={() => { convIdRef.current = null; closeWs(); setView("list"); loadConvs(); }}><ChevronLeft size={20} /></button>}
      <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center"><MessageCircle size={18} /></div>
      <div className="flex-1">
        <div className="font-bold text-sm">{cfg.title}</div>
        <div className="text-[11px] opacity-90 flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${online ? "bg-green-300" : "bg-slate-300"}`} />
          {online ? "Agents online" : "We'll reply soon"}
        </div>
      </div>
      {embed && <button data-testid="cx-close-button" onClick={() => window.parent.postMessage({ type: "snd-close" }, "*")}><X size={18} /></button>}
    </div>
  );

  const shell = (children) => (
    <div className={`cx-page ${embed ? "h-screen" : "min-h-screen flex items-center justify-center p-0 sm:p-6"}`} style={{ "--cx-accent": cfg.color }}>
      <div className={`bg-white flex flex-col overflow-hidden ${embed ? "h-full w-full" : "w-full h-[100dvh] sm:h-[640px] sm:max-w-md sm:rounded-2xl sm:shadow-2xl sm:border border-slate-200"}`}>
        {children}
      </div>
    </div>
  );

  if (!session) {
    return shell(
      <>
        <Header />
        <div className="flex-1 p-6 overflow-y-auto">
          <h2 className="font-heading font-bold text-xl text-slate-800">Hi there 👋</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">{cfg.greeting}</p>
          <form onSubmit={startSession} className="space-y-4">
            <div>
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Your name</label>
              <input data-testid="cx-name-input" className="cx-input mt-1" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Anita Gupta" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">Phone or email</label>
              <input data-testid="cx-contact-input" className="cx-input mt-1" required value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="98XXXXXXXX or you@email.com" />
            </div>
            {formErr && <p data-testid="cx-form-error" className="text-xs text-red-600 font-semibold">{formErr}</p>}
            <button data-testid="cx-start-button" className="cx-btn w-full py-2.5 text-sm">Start chatting</button>
          </form>
        </div>
      </>
    );
  }

  if (view === "list") {
    return shell(
      <>
        <Header />
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          <p className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Your conversations (last 30 days)</p>
          {convs.map((c) => (
            <button key={c.id} data-testid={`cx-conv-${c.id}`} onClick={() => openConv(c)} className="w-full text-left border border-slate-200 rounded-xl p-3 hover:border-blue-400 transition-colors">
              <div className="flex justify-between text-xs text-slate-400"><span className="font-bold text-slate-700">{c.ticket_number}</span><span>{fmtDate(c.last_message_at)}</span></div>
              <div className="text-sm text-slate-600 truncate mt-0.5">{c.last_message_preview || "New chat"}</div>
              <span className={`text-[10px] font-bold ${c.status === "open" ? "text-green-600" : "text-slate-400"}`}>{c.status === "open" ? "● Active" : "Closed"}</span>
            </button>
          ))}
          {convs.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No conversations yet</p>}
        </div>
        <div className="p-4 border-t border-slate-100">
          <button data-testid="cx-new-conversation-button" onClick={startNewConv} className="cx-btn w-full py-2.5 text-sm">Start new conversation</button>
        </div>
      </>
    );
  }

  return shell(
    <>
      <Header back />
      <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#FDFBF7]" data-testid="cx-chat-thread">
        {messages.map((m) => <Bubble key={m.id} m={m} />)}
        {typing && <div className="flex items-center gap-1.5 text-xs text-slate-400 pl-1"><span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" /><span className="ml-1">agent is typing…</span></div>}
        <div ref={bottomRef} />
      </div>
      {conv?.status === "open" && !messages.some((m) => m.sender === "customer") && (
        <div className="px-3 pb-2 pt-1 flex flex-wrap gap-1.5 bg-[#FDFBF7]" data-testid="cx-quick-options">
          {[["track_order", "Track My Order 📦"], ["payment_issue", "Payment Issue 💳"], ["product_issue", "Product Related Issue 🍼"], ["other", "Other Query 💬"]].map(([id, label]) => (
            <button key={id} data-testid={`cx-option-${id}`} onClick={() => pickOption(id)}
              className="text-xs font-semibold border-[1.5px] rounded-full px-3 py-1.5 bg-white hover:opacity-80 transition-opacity"
              style={{ borderColor: cfg.color, color: cfg.color }}>{label}</button>
          ))}
        </div>
      )}
      {conv?.status === "resolved" && (
        <div className="text-center py-2 bg-blue-50 border-t border-blue-100">
          {conv.csat_rating ? (
            <p data-testid="cx-csat-thanks" className="text-xs text-slate-600">Thanks for rating us <span className="text-amber-500">{"★".repeat(conv.csat_rating)}</span> — send a message to reopen this chat.</p>
          ) : (
            <div data-testid="cx-csat-prompt">
              <p className="text-xs font-semibold text-slate-600 mb-1">This chat was closed — how was our support?</p>
              <div className="flex justify-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} data-testid={`cx-csat-star-${n}`} onMouseEnter={() => setCsatSel(n)} onMouseLeave={() => setCsatSel(0)}
                    onClick={async () => { try { await axios.post(`${API}/widget/csat/${conv.id}`, { vt, rating: n }); setConv({ ...conv, csat_rating: n }); } catch (e) { /* ignore */ } }}
                    className={`text-xl leading-none ${n <= csatSel ? "text-amber-400" : "text-slate-300"} hover:scale-110 transition-transform`}>★</button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {uploading > 0 && (
        <div className="px-4 py-2 border-t border-slate-100 bg-blue-50" data-testid="cx-upload-progress">
          <div className="flex justify-between text-[11px] font-semibold text-slate-600 mb-1"><span>Uploading…</span><span>{uploading}%</span></div>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${uploading}%`, background: cfg.color }} /></div>
        </div>
      )}
      {uploadErr && (
        <div className="px-4 py-2 border-t border-red-100 bg-red-50 flex items-center justify-between gap-2" data-testid="cx-upload-error">
          <span className="text-xs text-red-600 font-semibold">{uploadErr}</span>
          <button data-testid="cx-upload-error-dismiss" onClick={() => setUploadErr("")} className="text-red-400 hover:text-red-600 shrink-0"><X size={14} /></button>
        </div>
      )}
      <form onSubmit={submit} className="flex items-center gap-2 p-3 border-t border-slate-100">
        <button type="button" data-testid="cx-attach-button" disabled={uploading > 0} className="p-2 text-slate-400 hover:text-blue-600 transition-colors disabled:opacity-40" onClick={() => fileRef.current?.click()}><Paperclip size={18} /></button>
        <input ref={fileRef} type="file" hidden accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; }} />
        <textarea data-testid="cx-composer-input" className="cx-input flex-1 resize-none" rows={Math.min(3, (text.match(/\n/g) || []).length + 1)} placeholder="Type your message…" value={text} onChange={(e) => onType(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(e); } }} />
        <button data-testid="cx-send-button" className="cx-btn p-2.5"><Send size={16} /></button>
      </form>
    </>
  );
}
